import React, { useState } from "react";
import {
  Card,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import Matrix6 from "../../img/logos/Matrix6.png";

const CustomKnowledgeCard = ({ card, onDelete }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleToggle = () => setOpen((prev) => !prev);



  const paragraphs = card.content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const closedCardSx = {
    p: 3,
    height: "9em",
    borderRadius: 3,
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.06)} 0%, ${alpha(
      theme.palette.common.white,
      0.98,
    )} 100%)`,
    boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.07)}`,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 14px 32px ${alpha(theme.palette.secondary.main, 0.14)}`,
    },
  };

  return (
    <Box sx={{ position: "relative" }}>
      {!open && (
        <Card onClick={handleToggle} elevation={0} sx={closedCardSx}>
          <Box
            component="img"
            src={Matrix6}
            alt="TON Logo"
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ ml: 2, flex: 1, fontSize: { xs: "0.9rem", sm: "1.1rem" }, lineHeight: 1.3 }}
          >
            {card.title}
          </Typography>
          <AutoAwesomeIcon sx={{ color: theme.palette.secondary.main, ml: 1 }} />
        </Card>
      )}

      {open && (
        <Card
          elevation={0}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: "85vw", md: "70vw" },
            maxHeight: "90vh",
            overflowY: "auto",
            zIndex: 2000,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.22)}`,
            background: "#eef2f7",
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              px: { xs: 2, md: 3 },
              py: 1.75,
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(
                theme.palette.common.white,
                0.94,
              )} 72%)`,
              borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
              backdropFilter: "blur(14px)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box
                component="img"
                src={Matrix6}
                alt="TON Logo"
                sx={{ width: 42, height: 42, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    lineHeight: 1,
                    color: alpha(theme.palette.secondary.main, 0.7),
                    fontSize: "0.62rem",
                  }}
                >
                  NML · Conocimiento
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.2,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  onDelete(card.id);
                  setOpen(false);
                }}
              >
                Delete
              </Button>
              <IconButton
                onClick={handleToggle}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.18) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Paper>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {paragraphs.map((paragraph, idx) => (
              <Typography key={`${card.id}-${idx}`} paragraph sx={{ lineHeight: 1.6 }}>
                {paragraph}
              </Typography>
            ))}
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default CustomKnowledgeCard;
